import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Center, Stack } from '@fiverr-private/layout_components';
import { Button } from '@fiverr-private/button';
import { Image } from '@fiverr-private/media';
import { ExternalLinkIcon } from '@fiverr-private/visuals';
import { getContext } from '@fiverr-private/fiverr_context';
import { ElementType } from '@fiverr-private/bianka_js';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { AppI18n } from '../../i18n';
import { useStepValidation } from '../../hooks/use-step-validation/useStepValidation';
import { useFetchInstallAppUrl } from '../../queries/useFetchInstallAppUrl';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { reportClickEvent } from '../../utils/report-bi-event/reportClickEvent';
import { StepTitle } from '../../components/step-title/StepTitle';
import { UserNotAuthorizedError } from '../../errors/userNotAuthorized';
import { verifyAuthCallback } from '../../services/verifyAuthCallback';
import { useStoreData } from '../../hooks/useStoreData';

const IMAGE_URL =
    'https://fiverr-res.cloudinary.com/image/upload/f_png,q_auto/v1/attachments/generic_asset/asset/b837ce997962f3d83dc06163352893af-1748634297866/Install%20BYS%20app.svg';

export const InstallApp: React.FC = () => {
    const { pageCtxId, userId } = getContext();
    const { currentPath } = useWizardNavigation();
    const [hasClickedInstall, setHasClickedInstall] = useState(false);
    const { registerStepValidation, unregisterStepValidation, registerValidationOnNext, unregisterValidationOnNext } =
        useStepValidation();
    const { data: installUrl } = useFetchInstallAppUrl();
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();
    const { storeData } = useStoreData();
    const [searchParams] = useSearchParams();

    const code = searchParams.get('code');
    const isUserReturnedFromShopify = !!code && !!storeData?.id;

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    const validationStatusFn = useCallback(() => (hasClickedInstall ? 'valid' : 'invalid'), [hasClickedInstall]);

    const validationOnNextFn = useCallback(async () => {
        const storeId = storeData?.id;

        if (!storeId || !code) return 'invalid';

        try {
            await verifyAuthCallback({ storeId, params: { code } });
            return 'valid';
        } catch (error) {
            if (error instanceof UserNotAuthorizedError) {
                return 'unauthenticated';
            }
            return 'invalid';
        }
    }, [code, storeData?.id]);

    // register validation for the current step
    useEffect(() => {
        if (!isUserReturnedFromShopify) {
            registerStepValidation('install-app', validationStatusFn);
        }

        return () => {
            unregisterStepValidation('install-app');
        };
    }, [isUserReturnedFromShopify, validationStatusFn, registerStepValidation, unregisterStepValidation]);

    // register validation for the next button click
    useEffect(() => {
        registerValidationOnNext('install-app', validationOnNextFn);

        return () => {
            unregisterValidationOnNext('install-app');
        };
    }, [validationOnNextFn, registerValidationOnNext, unregisterValidationOnNext]);

    const handleInstallClick = () => {
        setHasClickedInstall(true);
        reportClickEvent({
            biEventData: {
                userId,
                ctxId: pageCtxId,
            },
            eventType: 'clicked_on_install_app',
            elementType: ElementType.LINK,
            currentPath,
        });
        window.open(installUrl, '_blank');
    };

    return (
        <Center>
            <Stack direction="column" gap="8" color="grey_1200" ref={pageImpressionRef}>
                <StepTitle
                    title={<AppI18n k="steps.install_app.title" />}
                    description={<AppI18n k="steps.install_app.description" />}
                />
                <Image
                    src={IMAGE_URL}
                    width={{ default: 310, sm: 488, md: 514 }}
                    borderRadius="lg"
                    alt="Install BYS app"
                />
                <Button onClick={handleInstallClick} alignSelf="flexStart" variant="outline">
                    <AppI18n k="steps.install_app.cta" />
                    <ExternalLinkIcon />
                </Button>
            </Stack>
        </Center>
    );
};

InstallApp.displayName = 'InstallApp';
